import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { getMetricsForProject } from "@/data/projectMetrics";
import { getPublicMetricsForCaseStudy } from "@/data/caseStudyRepo";

/**
 * MetricsBlock — renders verified metrics for a case study.
 *
 * Two call modes:
 *   • <MetricsBlock caseStudy={cs} />  (preferred, Phase 4.0.1)
 *       Reads metrics directly off the merged case-study record. Admin-managed
 *       case studies carry inline metrics already filtered to approved-public.
 *   • <MetricsBlock projectSlug="unicrm-ai" />  (legacy fallback)
 *       Looks up code-managed metrics from projectMetrics.js.
 *
 * If NEITHER group has an approved metric, the whole component returns null.
 */
export default function MetricsBlock({ caseStudy, projectSlug }) {
  // Phase 4.0.1 — source-agnostic lookup when a case-study record is supplied.
  if (caseStudy) {
    const all = getPublicMetricsForCaseStudy(caseStudy);
    const outcomes = all.filter((m) => m.kind === "outcome" || !m.kind);
    const capabilities = all.filter((m) => m.kind === "capability");
    if (outcomes.length === 0 && capabilities.length === 0) return null;
    return (
      <MetricsBlockLayout
        slug={caseStudy.slug}
        outcomes={outcomes}
        capabilities={capabilities}
      />
    );
  }

  // Legacy fallback — projectSlug-only call keeps old call sites working.
  const outcomes = getMetricsForProject(projectSlug, { publicOnly: true, kind: "outcome" });
  const capabilities = getMetricsForProject(projectSlug, { publicOnly: true, kind: "capability" });
  const untyped = getMetricsForProject(projectSlug, { publicOnly: true }).filter(
    (m) => !m.kind,
  );
  const outcomeList = [...outcomes, ...untyped];
  if (outcomeList.length === 0 && capabilities.length === 0) return null;
  return (
    <MetricsBlockLayout
      slug={projectSlug}
      outcomes={outcomeList}
      capabilities={capabilities}
    />
  );
}

function MetricsBlockLayout({ slug, outcomes, capabilities }) {
  return (
    <section className="py-14 md:py-20" data-testid={`metrics-block-${slug}`}>
      <div className="max-w-6xl mx-auto px-6 space-y-14">
        {outcomes.length > 0 && (
          <div>
            <MetricsGroupHeader
              kicker="Operational outcomes"
              title="Verified evidence."
              description="Measured results from a specific implementation of this project. Numbers are contextualised — outcomes from a particular pilot/deployment, not generic guarantees for future customers."
            />
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid={`metrics-outcomes-${slug}`}>
              {outcomes.map((m, i) => <MetricCard key={m.id} m={m} i={i} accent="primary" />)}
            </div>
          </div>
        )}

        {capabilities.length > 0 && (
          <div>
            <MetricsGroupHeader
              kicker="Product capabilities"
              title="What ships in the product."
              description="Verified traits of the product itself. These describe what the platform does — they are not performance improvements or client outcomes."
            />
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid={`metrics-capabilities-${slug}`}>
              {capabilities.map((m, i) => <MetricCard key={m.id} m={m} i={i} accent="foreground" />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function MetricsGroupHeader({ kicker, title, description }) {
  return (
    <div className="max-w-2xl">
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">{kicker}</div>
      <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.1]">{title}</h2>
      <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function MetricCard({ m, i, accent = "primary" }) {
  const isBeforeAfter = m.before && m.after;
  const emphasis = accent === "primary" ? "text-primary" : "text-foreground";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
      className="rounded-3xl border border-border bg-card p-6 md:p-7"
      data-testid={`metric-${m.id}`}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">{m.label}</div>

      {isBeforeAfter ? (
        <div className="mt-4 flex items-center gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">Before</div>
            <div className="font-heading font-bold text-lg md:text-xl text-foreground/70 truncate">{m.before}</div>
          </div>
          <ArrowRight className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-primary/80">After</div>
            <div className="font-heading font-black text-xl md:text-2xl text-primary truncate">{m.after}</div>
          </div>
        </div>
      ) : (
        <div className={`mt-3 font-heading font-black text-3xl md:text-4xl leading-none ${emphasis}`}>
          {m.value}
        </div>
      )}

      {m.context && (
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{m.context}</p>
      )}
      {m.qualifier && (
        <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.2em] text-foreground/50">{m.qualifier}</div>
      )}
    </motion.div>
  );
}
